import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Check, X, Warning } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * WaiterOffersCard — shown on ProfessionalDashboard ONLY for Server/Waiter category users.
 * Lists pending booking offers with a 30-min countdown. Accept opens a confirmation modal
 * with a no-show warning before final commit.
 */
const WaiterOffersCard = () => {
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [confirmOffer, setConfirmOffer] = useState(null);
  const [acting, setActing] = useState(null);

  const isWaiter = user?.role === 'professional' && (user?.category === 'Server / Waiter' || user?.category === 'Server/Waiter');

  const fetchOffers = async () => {
    if (!isWaiter) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/waiter/pending-offers`, { withCredentials: true });
      setOffers(Array.isArray(data) ? data : []);
    } catch {
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOffers(); }, [isWaiter]);

  // Auto-refresh every 30s + tick every 1s for the countdown
  useEffect(() => {
    if (!isWaiter) return undefined;
    const refresh = setInterval(fetchOffers, 30000);
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [isWaiter]);

  const accept = async (offerId) => {
    setActing(offerId);
    try {
      await axios.post(`${BACKEND_URL}/api/waiter/offers/${offerId}/accept`, {}, { withCredentials: true });
      toast.success('Order accepted! Captain will confirm event details soon.');
      setConfirmOffer(null);
      fetchOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    } finally {
      setActing(null);
    }
  };

  const reject = async (offerId) => {
    if (!window.confirm('Reject this offer? Your turn counter still increases — every offer counts as an opportunity.')) return;
    setActing(offerId);
    try {
      await axios.post(`${BACKEND_URL}/api/waiter/offers/${offerId}/reject`, {}, { withCredentials: true });
      toast.success('Offer rejected. Next waiter in queue notified.');
      fetchOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setActing(null);
    }
  };

  if (!isWaiter || loading) return null;

  return (
    <>
      <Card className="bg-amber-50/30 border-amber-300" data-testid="waiter-offers-card">
        <CardHeader>
          <CardTitle className="font-heading text-2xl flex items-center gap-2">
            <Clock size={22} className="text-amber-600" /> Pending Order Offers
          </CardTitle>
          <CardDescription>
            New orders are offered in joined-date order. <strong>Confirm within the timer</strong> — every offer (accept / reject / no-response) counts as your turn for this round.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {offers.length === 0 ? (
            <p className="text-sm text-text-secondary py-3">No pending offers right now. Check back soon.</p>
          ) : (
            <div className="space-y-3">
              {offers.map((o) => {
                const exp = new Date(o.expires_at);
                const remainingMs = exp - now;
                const remaining = Math.max(0, Math.floor(remainingMs / 1000));
                const mins = Math.floor(remaining / 60);
                const secs = remaining % 60;
                const urgent = remaining < 300; // < 5 min
                return (
                  <div key={o.id} className="bg-white rounded border border-amber-300 p-4" data-testid={`offer-${o.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-text-primary">{o.package_name || 'Booking'}</div>
                        <div className="text-xs text-text-secondary">
                          Event: {o.event_date || '—'} {o.event_location ? `· ${o.event_location}` : ''}
                        </div>
                        {o.tier_name && (
                          <div className="text-xs mt-1">
                            <span className="text-text-secondary">Package: </span>
                            <span className="font-medium text-text-primary">{o.tier_name}</span>
                            {o.tier_price > 0 && <span className="text-brand-primary ml-2 font-bold">₹{Number(o.tier_price).toLocaleString('en-IN')}</span>}
                          </div>
                        )}
                      </div>
                      <div className={`flex-shrink-0 text-center px-3 py-2 rounded ${urgent ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-amber-100 text-amber-800'}`}>
                        <div className="text-[9px] tracking-[0.15em] uppercase font-bold">
                          {remaining > 0 ? 'Expires in' : 'Expired'}
                        </div>
                        <div className="font-mono font-bold text-lg" data-testid={`offer-countdown-${o.id}`}>
                          {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3 pt-2 border-t border-[#E8E1D5]">
                      <Button
                        onClick={() => setConfirmOffer(o)}
                        disabled={acting === o.id || remaining <= 0}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-none disabled:opacity-50"
                        data-testid={`accept-offer-${o.id}`}
                      >
                        <Check size={16} className="mr-1" /> Confirm Order
                      </Button>
                      <Button
                        onClick={() => reject(o.id)}
                        disabled={acting === o.id || remaining <= 0}
                        variant="outline"
                        className="rounded-none border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                        data-testid={`reject-offer-${o.id}`}
                      >
                        <X size={16} className="mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation modal with no-show warning */}
      {confirmOffer && (
        <div
          className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmOffer(null); }}
          data-testid="waiter-confirm-modal"
        >
          <div className="bg-white max-w-md w-full border-2 border-amber-400 shadow-2xl rounded">
            <div className="px-5 py-4 border-b border-[#E8E1D5] bg-amber-50">
              <div className="flex items-center gap-2">
                <Warning size={22} className="text-amber-600" weight="fill" />
                <h3 className="font-heading text-xl font-medium">Confirm Booking</h3>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="font-bold text-text-primary">{confirmOffer.package_name}</div>
                <div className="text-sm text-text-secondary">{confirmOffer.event_date} · {confirmOffer.event_location || 'Location TBD'}</div>
                {confirmOffer.tier_name && (
                  <div className="text-sm mt-1">
                    {confirmOffer.tier_name} {confirmOffer.tier_price > 0 ? `· ₹${Number(confirmOffer.tier_price).toLocaleString('en-IN')}` : ''}
                  </div>
                )}
              </div>
              <div className="bg-red-50 border-2 border-red-300 rounded p-4 text-sm">
                <div className="flex items-start gap-2 mb-2">
                  <Warning size={20} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="font-bold text-red-800">No-Show Warning</div>
                </div>
                <p className="text-red-900 leading-relaxed">
                  Once you confirm, you are <strong>committed to attend this event</strong>. A no-show will <strong>negatively affect your weekly bonus</strong> and may impact your priority in future order rounds.
                </p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  onClick={() => accept(confirmOffer.id)}
                  disabled={acting === confirmOffer.id}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-none"
                  data-testid="confirm-accept-btn"
                >
                  {acting === confirmOffer.id ? 'Confirming...' : 'I Understand — Confirm'}
                </Button>
                <Button
                  onClick={() => setConfirmOffer(null)}
                  variant="outline"
                  className="rounded-none border-[#E8E1D5]"
                  data-testid="confirm-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WaiterOffersCard;
